import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

/**
 * Navigation. Pass `overDark` on pages whose hero is a dark, full-bleed image
 * (the homepage) so the nav renders light at the top and flips to the cream bar
 * on scroll. Every other page keeps the default dark-on-cream nav.
 */
export default function Navigation({ overDark = false }) {
  const [scrolled, setScrolled] = useState(false);
  const mobileMenuRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // The three things we do come first and in order of what most people want,
  // then the supporting pages. Getting found sits second because it is the one
  // that can be bought on its own, so it has to be reachable without reading
  // the redesign page first.
  const navLinks = [
    { to: '/website-redesign', label: 'Website Redesign' },
    { to: '/the-new-era-of-search', label: 'New Era of Search' },
    { to: '/client-experience', label: 'Client Experience' },
    { to: '/work', label: 'Examples' },
    { to: '/blog', label: 'Writing' },
    { to: '/about', label: 'About' },
  ];

  // light treatment only when over a dark hero and not yet scrolled
  const light = overDark && !scrolled;
  const closeMobileMenu = () => {
    mobileMenuRef.current?.removeAttribute('open');
  };

  const handleMobileMenuKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    closeMobileMenu();
    mobileMenuRef.current?.querySelector('summary')?.focus();
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-[rgba(10,26,47,0.92)] backdrop-blur-2xl border-b border-[#F5F3EF]/10 shadow-[0_16px_45px_-30px_rgba(0,0,0,0.85)]'
          : 'bg-transparent border-b border-transparent'
      }`}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 md:px-12 lg:px-16 py-4 sm:py-5">
        <div className="flex items-center justify-between gap-6 lg:gap-10">
          <Link to="/" className="flex shrink-0 items-center gap-2.5" aria-label="Academy Legacy">
            <span
              className={`font-serif text-[21px] sm:text-[25px] tracking-[-0.025em] leading-none transition-colors ${light || scrolled ? 'text-[#F5F3EF]' : 'text-[#0A1A2F]'}`}
            >
              Academy <span className="italic text-[#D4AF37]">Legacy</span>
            </span>
          </Link>

          {/* Six links plus Contact needs 980px and 1024 only offers 896, so the
              full bar starts at xl and 1024 gets the menu button instead. The
              alternative was 13px labels or a tighter container, and neither is
              worth it on a site selling design. Labels never wrap: two-line nav
              items read as four headings rather than six links. */}
          <div className="hidden xl:flex items-center gap-7 2xl:gap-9">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`whitespace-nowrap text-[14px] tracking-[0.04em] transition-colors duration-200 ${
                  light || scrolled ? 'text-[#F5F3EF]/70 hover:text-[#F5F3EF]' : 'text-[#0A1A2F]/[0.65] hover:text-[#0A1A2F]'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/contact"
              className={`whitespace-nowrap text-[11px] tracking-[0.22em] uppercase font-semibold border-b pb-0.5 transition-colors ${
                light || scrolled
                  ? 'text-[#F5F3EF] border-[#D4AF37] hover:text-[#D4AF37]'
                  : 'text-[#0A1A2F] border-[#C5A059] hover:text-[#8C6C2D]'
              }`}
            >
              Contact
            </Link>
          </div>

          <details ref={mobileMenuRef} onKeyDown={handleMobileMenuKeyDown} className="group relative xl:hidden">
            <summary
              className={`flex h-11 w-11 list-none cursor-pointer items-center justify-center rounded-full transition-colors [&::-webkit-details-marker]:hidden ${light || scrolled ? 'text-[#F5F3EF]/80 hover:bg-white/10 hover:text-[#F5F3EF]' : 'text-[#0A1A2F]/70 hover:bg-[#0A1A2F]/10 hover:text-[#0A1A2F]'}`}
              aria-label="Menu"
            >
              <Menu className="h-6 w-6 group-open:hidden" />
              <X className="hidden h-6 w-6 group-open:block" />
            </summary>
            <div className="absolute right-0 top-12 w-[min(84vw,320px)] space-y-3 rounded-[22px] border border-[#F5F3EF]/[0.12] bg-[rgba(10,26,47,0.97)] px-6 py-5 shadow-2xl backdrop-blur-2xl">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={closeMobileMenu}
                  className="block min-h-11 border-b border-[#F5F3EF]/[0.08] py-2.5 text-base text-[#F5F3EF]/[0.76] hover:text-[#F5F3EF]"
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/contact"
                onClick={closeMobileMenu}
                className="block text-[11px] tracking-[0.19em] uppercase font-semibold text-[#D4AF37] pt-3"
              >
                Send us your site →
              </Link>
            </div>
          </details>
        </div>
      </div>
    </nav>
  );
}
